import {ContentKeyPair, FunnelKey, RoleKeyPair} from "./Keys";
import {CjmapSelection} from "./CjmapSelection";

type UserMap = Map<string, string>;
type FocusMap = Map<string, string[]>;


export class CjmapCollaboratorFocus {

    // user_id => email
    private readonly user_map: UserMap;
    // content_key_pair -> user_id[]
    private readonly focus_map: FocusMap;

    private constructor(user_map: UserMap, focus_map: FocusMap) {
        this.user_map = user_map;
        this.focus_map = focus_map;
    }

    get userCount() {
        return this.user_map.size;
    }

    updateUser(user_id: string, user_mail: string): CjmapCollaboratorFocus {
        const new_user_map = new Map(this.user_map);
        new_user_map.set(user_id, user_mail);
        return new CjmapCollaboratorFocus(new_user_map, this.focus_map);
    }

    removeUser(user_id: string): CjmapCollaboratorFocus {
        const new_user_map = new Map(this.user_map);
        new_user_map.delete(user_id);
        return new CjmapCollaboratorFocus(new_user_map, this._makeUserRemovedFocusMap(user_id));
    }

    listAllUserLabel(): string[] {
        return Array.from(this.user_map.values());
    }

    updateFocus(user_id: string, selection: CjmapSelection): CjmapCollaboratorFocus {
        const key = selection.toSelectedPositionString();
        const new_focus_map = this._makeUserRemovedFocusMap(user_id);

        const users = new_focus_map.get(key) || [];
        users.push(user_id);
        new_focus_map.set(key, users);
        return new CjmapCollaboratorFocus(this.user_map, new_focus_map);
    }

    removeFocus(user_id: string): CjmapCollaboratorFocus {
        return new CjmapCollaboratorFocus(this.user_map, this._makeUserRemovedFocusMap(user_id));
    }

    private _makeUserRemovedFocusMap(user_id: string): FocusMap {
        const new_focus_map = new Map() as FocusMap;
        this.focus_map.forEach((users, key) => {
            new_focus_map.set(key, users.filter(u => u !== user_id));
        });
        return new_focus_map;
    }


    static createNew(): CjmapCollaboratorFocus {
        return new CjmapCollaboratorFocus(new Map(), new Map());
    }

    getFocusUserLabelsInRoleCell(role_key_pair: RoleKeyPair) {
        return this.getFocusUserLabels(CjmapSelection.createNew().selectRoleCell(role_key_pair));
    }
    getFocusUserLabelsInFunnelGroupCell(funnel_key: FunnelKey) {
        return this.getFocusUserLabels(CjmapSelection.createNew().selectFunnelGroupCell(funnel_key));
    }

    getFocusUserLabelsInMainFunnelCell(funnel_key: FunnelKey) {
        return this.getFocusUserLabels(CjmapSelection.createNew().selectMainFunnelCell(funnel_key));
    }

    getFocusUserLabelsInContentCell(content_key_pair: ContentKeyPair) {
        return this.getFocusUserLabels(CjmapSelection.createNew().selectContentCell(content_key_pair));
    }

    private getFocusUserLabels(selection: CjmapSelection) {
        const users = this.focus_map.get(selection.toSelectedPositionString());
        if (users === undefined || users.length === 0) {
            return [];
        }

        return users.map(u => {
            const mail = this.user_map.get(u);
            return mail || `(${u})`;
        })
    }

    hasUser(user_id: string) {
        return this.user_map.has(user_id);
    }
}
